import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  CheckIcon,
  CalendarIcon,
  BeakerIcon,
  ShieldCheckIcon,
  HeartIcon,
  PhotoIcon,
  HomeIcon,
  ScissorsIcon,
  IdentificationIcon,
  HashtagIcon,
  UserIcon,
  TagIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

interface Dog {
  dog_id: number;
  hundename: string;
  vollstaendiger_name?: string;
  rasse?: string;
  geburtstag?: string;
  chipnummer?: string;
  tassonummer?: string;
  tollwut_end_date?: string;
  geschlecht?: string;
  kastriert?: boolean;
  hundebox?: string;
  insurance_name?: string;
  valid_tollwut: boolean;
  valid_insurance: boolean;
  tollwut_checked_at?: string;
  insurance_checked_at?: string;
  last_audit_insurance?: string;
  last_audit_tollwut?: string;
  profile_image?: string;
  image_path?: string; // Add this field from API response
  image_path_updated?: string; // Timestamp for cache busting
}

interface AddDogModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  onSubmit: (formData: FormData) => Promise<void>;
}

interface EditDogModalProps {
  isOpen: boolean;
  onClose: () => void;
  dog: Dog;
  onSubmit: (formData: FormData, dogId: number) => Promise<void>;
}

export const AddDogModal: React.FC<AddDogModalProps> = ({ isOpen, onClose, memberId, onSubmit }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => { document.body.style.overflow = ''; clearTimeout(timer); };
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    await onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-3xl bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
            <HeartIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Hund hinzufügen</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4">
          {/* Profilbild */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Hund Profilbild"
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 dark:border-gray-600"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <PhotoIcon className="h-12 w-12 text-gray-400" />
                </div>
              )}
              <label className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full cursor-pointer transition-colors">
                <PhotoIcon className="h-5 w-5" />
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Basisdaten */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <HeartIcon className="h-5 w-5 text-primary-600" />
              Basisdaten
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HeartIcon className="h-4 w-4 text-primary-600" />
                    Rufname *
                  </div>
                </label>
                <input
                  type="text"
                  name="rufname"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <IdentificationIcon className="h-4 w-4 text-primary-600" />
                    Vollständiger Name
                  </div>
                </label>
                <input
                  type="text"
                  name="vollstaendiger_name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <TagIcon className="h-4 w-4 text-primary-600" />
                    Rasse
                  </div>
                </label>
                <input
                  type="text"
                  name="rasse"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-primary-600" />
                    Geburtstag
                  </div>
                </label>
                <input
                  type="date"
                  name="geburtstag"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-primary-600" />
                    Geschlecht
                  </div>
                </label>
                <select
                  name="geschlecht"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Rüde">Rüde</option>
                  <option value="Hündin">Hündin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <ScissorsIcon className="h-4 w-4 text-primary-600" />
                    Kastriert
                  </div>
                </label>
                <select
                  name="kastriert"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Nein">Nein</option>
                  <option value="Ja">Ja</option>
                </select>
              </div>
            </div>
          </div>

          {/* Registrierung */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <IdentificationIcon className="h-5 w-5 text-primary-600" />
              Registrierung
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HashtagIcon className="h-4 w-4 text-primary-600" />
                    Chipnummer
                  </div>
                </label>
                <input
                  type="text"
                  name="chipnummer"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HashtagIcon className="h-4 w-4 text-primary-600" />
                    Tasso-Nummer
                  </div>
                </label>
                <input
                  type="text"
                  name="tassonummer"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Gesundheit & Extras */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
              Gesundheit & Extras
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <BeakerIcon className="h-4 w-4 text-primary-600" />
                    Tollwut gültig bis
                  </div>
                </label>
                <input
                  type="date"
                  name="impfstatus_tollwut"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <ShieldCheckIcon className="h-4 w-4 text-primary-600" />
                    Versicherung
                  </div>
                </label>
                <input
                  type="text"
                  name="insurance_name"
                  placeholder="Name der Versicherung"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HomeIcon className="h-4 w-4 text-primary-600" />
                    Hundebox
                  </div>
                </label>
                <select
                  name="hundebox"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Ja">Ja</option>
                  <option value="Nein">Nein</option>
                </select>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Hinzufügen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const EditDogModal: React.FC<EditDogModalProps> = ({ isOpen, onClose, dog, onSubmit }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => { document.body.style.overflow = ''; clearTimeout(timer); };
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);
  const [tollwutChecked, setTollwutChecked] = useState(false);
  const [insuranceChecked, setInsuranceChecked] = useState(false);

  // Set initial image when dog changes or modal opens
  useEffect(() => {
    // If dog has an image_path, construct the proper API URL
    if (dog.image_path && dog.dog_id) {
      // Get JWT token from localStorage
      const token = localStorage.getItem('jwt_token');
      const timestamp = dog.image_path_updated || new Date().getTime();
      
      // Construct the image URL using the API endpoint format
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const imageUrl = `${apiUrl}/api/dogs/image-url/${dog.dog_id}?size=M&token=${token}&t=${timestamp}`;
      
      setImagePreview(imageUrl);
    } else if (dog.profile_image) {
      // Fallback to profile_image if available
      setImagePreview(dog.profile_image);
    } else {
      setImagePreview(null);
    }
  }, [dog]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Add audit checkboxes
    if (tollwutChecked) {
      formData.append('tollwut_checked', 'true');
    }
    if (insuranceChecked) {
      formData.append('insurance_checked', 'true');
    }
    
    await onSubmit(formData, dog.dog_id);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-3xl bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
            <HeartIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Hund bearbeiten</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4">
          {/* Profilbild */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Hund Profilbild"
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 dark:border-gray-600"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <PhotoIcon className="h-12 w-12 text-gray-400" />
                </div>
              )}
              <label className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full cursor-pointer transition-colors">
                <PhotoIcon className="h-5 w-5" />
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Basisdaten */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <HeartIcon className="h-5 w-5 text-primary-600" />
              Basisdaten
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HeartIcon className="h-4 w-4 text-primary-600" />
                    Rufname *
                  </div>
                </label>
                <input
                  type="text"
                  name="rufname"
                  defaultValue={dog.hundename}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <IdentificationIcon className="h-4 w-4 text-primary-600" />
                    Vollständiger Name
                  </div>
                </label>
                <input
                  type="text"
                  name="vollstaendiger_name"
                  defaultValue={dog.vollstaendiger_name}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <TagIcon className="h-4 w-4 text-primary-600" />
                    Rasse
                  </div>
                </label>
                <input
                  type="text"
                  name="rasse"
                  defaultValue={dog.rasse}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-primary-600" />
                    Geburtstag
                  </div>
                </label>
                <input
                  type="date"
                  name="geburtstag"
                  defaultValue={dog.geburtstag?.split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-primary-600" />
                    Geschlecht
                  </div>
                </label>
                <select
                  name="geschlecht"
                  defaultValue={dog.geschlecht}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Rüde">Rüde</option>
                  <option value="Hündin">Hündin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <ScissorsIcon className="h-4 w-4 text-primary-600" />
                    Kastriert
                  </div>
                </label>
                <select
                  name="kastriert"
                  defaultValue={dog.kastriert ? 'Ja' : 'Nein'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Nein">Nein</option>
                  <option value="Ja">Ja</option>
                </select>
              </div>
            </div>
          </div>

          {/* Registrierung */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <IdentificationIcon className="h-5 w-5 text-primary-600" />
              Registrierung
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HashtagIcon className="h-4 w-4 text-primary-600" />
                    Chipnummer
                  </div>
                </label>
                <input
                  type="text"
                  name="chipnummer"
                  defaultValue={dog.chipnummer}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HashtagIcon className="h-4 w-4 text-primary-600" />
                    Tasso-Nummer
                  </div>
                </label>
                <input
                  type="text"
                  name="tassonummer"
                  defaultValue={dog.tassonummer}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Gesundheit & Extras */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
              Gesundheit & Extras
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <BeakerIcon className="h-4 w-4 text-primary-600" />
                    Tollwut gültig bis
                  </div>
                </label>
                <input
                  type="date"
                  name="impfstatus_tollwut"
                  defaultValue={dog.tollwut_end_date?.split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <ShieldCheckIcon className="h-4 w-4 text-primary-600" />
                    Versicherung
                  </div>
                </label>
                <input
                  type="text"
                  name="insurance_name"
                  defaultValue={dog.insurance_name}
                  placeholder="Name der Versicherung"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <HomeIcon className="h-4 w-4 text-primary-600" />
                    Hundebox
                  </div>
                </label>
                <select
                  name="hundebox"
                  defaultValue={dog.hundebox || ''}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Ja">Ja</option>
                  <option value="Nein">Nein</option>
                </select>
              </div>
            </div>
          </div>

          {/* Audit Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-primary-600" />
              Audit
            </h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                <input
                  type="checkbox"
                  checked={tollwutChecked}
                  onChange={(e) => setTollwutChecked(e.target.checked)}
                  className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Tollwutimpfung wurde geprüft
                </span>
                {dog.last_audit_tollwut && (
                  <span className="text-xs text-gray-500 ml-auto">
                    Letzte Prüfung: {new Date(dog.last_audit_tollwut).toLocaleDateString('de-DE')}
                  </span>
                )}
              </label>

              <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                <input
                  type="checkbox"
                  checked={insuranceChecked}
                  onChange={(e) => setInsuranceChecked(e.target.checked)}
                  className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Versicherung wurde geprüft
                </span>
                {dog.last_audit_insurance && (
                  <span className="text-xs text-gray-500 ml-auto">
                    Letzte Prüfung: {new Date(dog.last_audit_insurance).toLocaleDateString('de-DE')}
                  </span>
                )}
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <CheckIcon className="h-5 w-5" />
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};